/*
  covered.ro · Black Friday 1+1: keeps the free "Skin GRATUIT" line equal to the paid skins in the cart.
  Loaded by snippets/cv-head.liquid when snippets/cv-bf-active says so (Theme settings › covered · Black Friday
  1+1: gift product set; campaign ticked with valid dates, from a day before the start to the end; or a gift
  line in the cart).

  Source of truth: snippets/cv-bf-state (Liquid) = { eligible, gifts: [{ key, qty, final, orig, unit }], lines },
  printed inline with the page and bundled as section "cv-bf-state" with every theme cart request
  (cart:grouped-sections), so the usual add / change / remove costs no extra request.
  - Inside the window (ticked, start <= now < end, Europe/Bucharest wall time "YYYY-MM-DD HH:MM[:SS]", both
    required): gift quantity = eligible. Outside it: 0, so a leftover gift line is removed.
  - 0-lei guard: every write returns the cart with its real prices. A gift unit the discount does not make free
    is taken back at once and that cap holds (sessionStorage, 10 min, same paid count): the free skin is never
    charged. In the first 15 minutes after the start (the visitor's clock may run ahead of Shopify's) the cap
    is not kept and the free skin is tried again after 2.5 s, 5 s, 10 s... A partial cap is retried when more
    skins come in. While a cap holds, html.cv-bf-short shows a short note in the drawer and on /cart.
  - One run at a time, bursts coalesced, no write when the cart already matches. Something else resetting the
    gift line twice, two failed writes in a row, or 20 writes in a minute stop new free skins for the page;
    taking back a gift line that is not free always stays possible. Failed reads are retried (1 s, 2 s, 4 s).
  - Paths that skip the theme's event bus (Advanced Product Options, other tabs, back/forward cache, an app's
    add whose response is never read) are noticed read-only: PerformanceObserver entries for
    /cart/add|change|update|clear, pageshow, visibilitychange and the drawer opening. The state is then read
    with GET {cart_url}?sections=cv-bf-state. When the cart differs from what the drawer / cart page show, they
    are re-rendered (Section Rendering API + /cart.js, through FoxTheme.pubsub like gift-wrapping.js).
  - Checkout: waits for a running sync, reads the cart one last time and fixes it if needed (outside the usual
    limits), and drops the positional updates[] of the /cart form when its rows are no longer the cart's lines
    (stale tab), so a stale form never changes quantities behind the free skin's back.
  No touch or click handlers (theme.js replays taps).
*/
(() => {
  if (window.cvBfGift) return;
  let cfg = null;
  try {
    cfg = JSON.parse(document.getElementById('cv-bf-config').textContent);
  } catch (e) {
    return;
  }
  const VID = Number(cfg && cfg.variant);
  if (!VID) return;

  const SECTION = 'cv-bf-state';
  const SRC = 'cv-bf-gift';
  const SLACK = 50; // ms between a cart:grouped-sections event and the start of its request
  const D = document;
  const HTML = D.documentElement;
  const now = () => performance.now();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const warn = (...a) => console.warn('[covered · Black Friday 1+1]', ...a);

  // Window. "YYYY-MM-DD HH:MM[:SS]" is Bucharest wall time: UTC+3 from the last Sunday of March 01:00 UTC to the
  // last Sunday of October 01:00 UTC, else UTC+2 (the 03:xx hour that does not exist in March and the repeated
  // 03:xx hour in October both count as summer time). With Z or an offset the value is taken as is.
  // Same rules as snippets/cv-bf-epoch.liquid.
  const toEpoch = (v) => {
    const m = /^(\d{4})-(\d\d)-(\d\d)[ T](\d\d):(\d\d)(?::(\d\d))?(Z|[+-]\d\d:?\d\d)?$/.exec(String(v || '').trim());
    if (!m) return NaN;
    const [y, mo, d, h, mi, se] = m.slice(1, 7).map((x) => Number(x || 0));
    const wall = Date.UTC(y, mo - 1, d, h, mi, se);
    const w = new Date(wall);
    if (y < 2020 || w.getUTCMonth() !== mo - 1 || w.getUTCDate() !== d || h > 23 || mi > 59 || se > 59) return NaN;
    const z = m[7];
    if (z === 'Z') return wall;
    if (z) {
      const zh = Number(z.slice(1, 3));
      const zm = Number(z.slice(-2));
      return zh > 14 || zm > 59 ? NaN : wall - (z[0] === '-' ? -1 : 1) * (zh * 60 + zm) * 6e4;
    }
    const lastSunday = (mon) => {
      const e = new Date(Date.UTC(y, mon + 1, 0));
      return Date.UTC(y, mon, e.getUTCDate() - e.getUTCDay(), 1);
    };
    return wall - 72e5 >= lastSunday(2) && wall - 108e5 < lastSunday(9) ? wall - 108e5 : wall - 72e5;
  };
  const START = toEpoch(cfg.start);
  const END = toEpoch(cfg.end);
  const valid = cfg.on === true && START < END;
  if (cfg.on === true && !valid && window.Shopify && window.Shopify.designMode) warn('start/end missing or invalid, the free skin is not added', cfg.start, cfg.end);
  const inWindow = () => valid && Date.now() >= START && Date.now() < END;
  const nearStart = () => valid && Date.now() - START < 9e5;

  // State
  const parse = (html) => {
    const m = /<script[^>]*data-cv-bf-state[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
    try {
      const s = m && JSON.parse(m[1]);
      return s && Number.isFinite(s.eligible) && Array.isArray(s.gifts) ? s : null;
    } catch (e) {
      return null;
    }
  };
  const sigOf = (s) => (s && Array.isArray(s.lines) ? s.lines.map((l) => l.join(':')).join('|') : null);
  const sigOfCart = (c) => (c && Array.isArray(c.items) ? c.items.map((i) => `${i.key}:${i.quantity}`).join('|') : null);
  let state = null;
  let freshAt = -1; // start (performance.now) of the request that produced `state`; 0 = the page itself
  let dirtyAt = -1; // start of the latest cart write by someone else
  let groupedAt = -1; // last cart:grouped-sections, i.e. a theme cart request starting
  let busy = null;
  let again = false;
  let timer = 0;
  let cap = Infinity; // free units the discount really gives (0-lei guard)
  let capE = 0; // paid units when the cap was set
  let stopped = false; // no new free skins on this page (taking back units that are not free stays possible)
  let fails = 0; // failed reads in a row
  let wfails = 0; // failed writes in a row
  let reverts = 0;
  let tries = 0; // free-skin retries right after the start
  let retryT = 0;
  let failT = 0;
  let done = null; // { eligible, qty } after our last write
  let shown = null; // signature of the cart the drawer / cart page show
  let good = null; // { at, cart, sections }: a full render of the current state (to show it again, no request)
  const writes = [];
  const accept = (s, at) => {
    if (s && at >= freshAt) {
      state = s;
      freshAt = at;
    }
  };
  const qty = (gifts) => gifts.reduce((n, g) => n + g.qty, 0);
  const free = (gifts) => gifts.reduce((n, g) => n + (g.final <= 0 ? g.qty : g.unit > 0 ? Math.min(g.qty, Math.floor((g.orig - g.final) / g.unit + 1e-6)) : 0), 0);
  const priced = (gifts) => gifts.some((g) => g.final > 0);
  const want = () => (inWindow() ? Math.min(state.eligible, cap) : 0);
  const relevant = () => !state || inWindow() || state.gifts.length > 0;
  const stale = () => dirtyAt > freshAt + SLACK;
  const stop = (why) => {
    if (!stopped) warn(why);
    stopped = true;
  };
  const short = () => HTML.classList.toggle('cv-bf-short', !!state && inWindow() && cap < state.eligible && !(nearStart() && tries < 3));

  const KEY = 'cv-bf-gift';
  const remember = () => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ v: VID, e: state.eligible, cap, t: Date.now() }));
    } catch (e) {}
  };
  const recall = () => {
    try {
      const r = JSON.parse(sessionStorage.getItem(KEY));
      if (r && r.v === VID && Date.now() - r.t < 6e5 && r.t - START >= 9e5 && r.e === state.eligible && r.cap >= 0) {
        cap = r.cap;
        capE = r.e;
      }
    } catch (e) {}
  };

  // 0-lei guard: units the discount leaves (partly) paid are not free. Only judged while the gift count does not
  // exceed the paid skins, so a count that is simply out of date is not taken for a failure.
  const trip = (gifts) => {
    if (!priced(gifts)) return;
    const f = free(gifts);
    if (f >= cap) return;
    cap = f;
    capE = state.eligible;
    if (nearStart()) {
      // Shopify's discount may start a little after the visitor's clock says so: try again soon.
      clearTimeout(retryT);
      retryT = setTimeout(() => {
        cap = Infinity;
        markDirty(now());
      }, Math.min(2500 * 2 ** tries++, 12e4));
    } else remember();
    warn(f ? `the discount makes only ${f} skin(s) free; the rest is removed` : 'Skin GRATUIT is not 0 lei (automatic discount missing or inactive): removed');
  };

  // Requests (same origin, cart routes only)
  const routes = () => (window.FoxTheme && window.FoxTheme.routes) || {};
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = () => routes().cart_url || `${root()}cart`;
  const call = async (url, body) => {
    const opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    try {
      const r = await fetch(url, opts);
      return { ok: r.ok, status: r.status, body: await r.json().catch(() => null) };
    } catch (e) {
      return { ok: false, status: 0, body: null };
    }
  };
  const why = (r) => [r.status, r.body && (r.body.description || r.body.message)];
  // A failed read is retried after 1 s, 2 s, 4 s; after that the next cart event tries again.
  const failed = (what, r) => {
    warn(`${what} failed`, ...why(r));
    if (++fails <= 3) {
      clearTimeout(failT);
      failT = setTimeout(() => markDirty(now()), 1000 * 2 ** (fails - 1));
    }
    return null;
  };
  // A failed write is retried once (after a fresh read); two in a row stop new free skins for this page.
  const wfailed = (r) => {
    warn('updating the cart failed', ...why(r));
    if (++wfails > 1) stop('the cart keeps refusing updates: no new free skins on this page');
    else {
      dirtyAt = now();
      again = true;
    }
    return null;
  };
  // The sections the theme renders for a cart change (drawer, /cart), ours first.
  const grouped = () => {
    const sections = [];
    HTML.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
    return [SECTION, ...sections.filter((x) => x !== SECTION)];
  };

  const read = async () => {
    const t = now();
    const r = await call(`${cartUrl()}?sections=${SECTION}`);
    const s = r.ok && r.body && parse(r.body[SECTION]);
    if (!s) return failed('reading the cart state', r);
    fails = 0;
    accept(s, t);
    return s;
  };

  // Re-render the drawer / cart page through the theme's own path: their sections rendered for this page (the
  // drawer renders nothing in the /cart context) plus /cart.js for the cart object the theme expects.
  const fetchRender = async () => {
    const list = grouped().join(',');
    const t = now();
    const [r, c] = await Promise.all([call(`${location.pathname}?sections=${list}`), call(`${cartUrl()}.js`)]);
    const s = r.ok && r.body && parse(r.body[SECTION]);
    if (!s || !c.ok || !c.body || !Array.isArray(c.body.items)) return failed('reading the cart', s ? c : r);
    fails = 0;
    accept(s, t);
    return freshAt === t && sigOfCart(c.body) === sigOf(s) ? { cart: c.body, sections: r.body } : null;
  };

  const write = async (n) => {
    const sections = grouped();
    const updates = {};
    if (state.gifts.length) state.gifts.forEach((g, i) => (updates[g.key] = i ? 0 : n));
    else updates[VID] = n;
    writes.push(Date.now());
    const t = now();
    const w = await call(`${routes().cart_update_url || `${root()}cart/update`}.js`, { updates, sections, sections_url: location.pathname });
    if (!w.ok || !w.body) return wfailed(w);
    wfails = 0;
    // The update returns the cart with its real prices; /cart.js only when it does not.
    let cart = Array.isArray(w.body.items) ? w.body : null;
    if (!cart) {
      const c = await call(`${cartUrl()}.js`);
      if (!c.ok || !c.body || !Array.isArray(c.body.items)) return failed('reading the cart', c);
      cart = c.body;
    }
    const s = parse(w.body.sections && w.body.sections[SECTION]);
    const gifts = s
      ? s.gifts
      : cart.items.filter((i) => Number(i.variant_id) === VID).map((i) => ({ key: i.key, qty: i.quantity, final: i.final_line_price, orig: i.original_line_price, unit: i.original_price }));
    accept({ eligible: s ? s.eligible : state.eligible, gifts, lines: s ? s.lines : cart.items.map((i) => [i.key, i.quantity]) }, t);
    // A newer state arrived meanwhile (a theme request started after ours): its render wins.
    if (freshAt !== t) return (done = null), 'stale';
    done = { eligible: state.eligible, qty: qty(gifts) };
    good = { at: t, cart, sections: w.body.sections || {} };
    return good;
  };

  const publish = (r, raised) => {
    const ps = window.FoxTheme && window.FoxTheme.pubsub;
    if (!ps) return;
    shown = sigOfCart(r.cart);
    const ae = D.activeElement;
    const id = ae && ae.id;
    const x = window.scrollX;
    const y = window.scrollY;
    ps.publish(ps.PUB_SUB_EVENTS.cartUpdate, { cart: Object.assign({}, r.cart, { sections: r.sections }), sources: SRC });
    // cart.js moves focus to the first line after an update it did not start: keep the visitor's place.
    const cur = D.activeElement;
    if (cur !== ae) {
      const back = ae && ae !== D.body && (ae.isConnected ? ae : id && D.getElementById(id));
      if (back) back.focus({ preventScroll: true });
      else if (cur && cur.blur) cur.blur();
    }
    if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
    // ...and it traps Tab inside the drawer or the /cart table: only wanted while the drawer is open.
    const drawer = D.querySelector('cart-drawer');
    if (!(drawer && drawer.open) && window.FoxTheme.a11y) window.FoxTheme.a11y.removeTrapFocus();
    if (raised) D.querySelectorAll('[data-cv-bf-gift]').forEach((el) => el.classList.add('cv-bf-gift--in'));
  };

  // The quantity the gift line should have now, or null when nothing has to be written.
  // force (checkout): outside the usual limits.
  const target = (force) => {
    if (inWindow() && qty(state.gifts) <= state.eligible) trip(state.gifts);
    if (cap > 0 && cap < Infinity && state.eligible > capE) cap = Infinity; // more skins: the discount may free more
    const have = qty(state.gifts);
    const n = want();
    if (have === n && state.gifts.length <= 1) return null;
    // Stopped: only take back units that are not free (a free unit left over costs the customer nothing).
    if (stopped && !force && !(n < have && priced(state.gifts))) return null;
    return n;
  };

  const run = async (force) => {
    if ((!state || stale()) && !(await read())) return;
    let last = null;
    let raised = false;
    for (let i = 0; i < 4; i++) {
      const n = target(force);
      if (n === null) break;
      const have = qty(state.gifts);
      if (!force) {
        // Someone else (another app, another tab, the customer's remove link) set the gift back while the paid
        // skins stayed the same: twice is a tug of war. A hard cap of 20 writes a minute backs it up.
        if (done && done.eligible === state.eligible && done.qty !== have && ++reverts >= 2) stop('Skin GRATUIT keeps being changed by something else: no new free skins on this page');
        if (writes.filter((t) => Date.now() - t < 6e4).length >= 20) stop('too many cart updates: no new free skins on this page');
        if (target(false) === null) break;
      }
      const r = await write(n);
      if (!r) break; // failed: the last good result stays shown
      if (r === 'stale') {
        last = null;
        continue;
      }
      last = r;
      raised = raised || n > have;
    }
    short();
    if (last) return publish(last, raised && qty(state.gifts) > 0);
    // Nothing to write, but the drawer / cart page show another cart (other tab, an app, responses that came
    // back out of order): show the cart as it is.
    const sig = sigOf(state);
    if (!stopped && sig !== null && shown !== null && sig !== shown) {
      // An older response was rendered after the current one (out of order): show the current one again.
      if (good && good.at === freshAt && sigOfCart(good.cart) === sig) return publish(good, false);
      const r = await fetchRender();
      if (!r) return;
      if (target(force) !== null) again = true;
      else publish(r, false);
    }
  };

  const kick = (force) => {
    clearTimeout(timer);
    timer = 0;
    if (busy) {
      again = true;
      return busy;
    }
    HTML.classList.add('cv-bf-busy');
    busy = run(force)
      .catch((e) => warn(e))
      .finally(() => {
        busy = null;
        HTML.classList.remove('cv-bf-busy');
        if (again) {
          again = false;
          schedule(0);
        }
      });
    return busy;
  };
  const schedule = (ms) => {
    clearTimeout(timer);
    timer = setTimeout(kick, ms);
  };
  // Once stopped, only the theme's own cart events (and checkout) still run the script.
  const markDirty = (at) => {
    if (at > dirtyAt) dirtyAt = at;
    if (relevant() && !stopped) schedule(400);
  };

  // Hooks that must exist before the first theme cart request
  D.addEventListener('cart:grouped-sections', (e) => {
    groupedAt = now();
    const s = e.detail && e.detail.sections;
    if (Array.isArray(s) && !s.includes(SECTION)) s.push(SECTION);
  });
  const WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((e) => {
        let path = '';
        try {
          path = new URL(e.name, location.href).pathname;
        } catch (x) {}
        if (WRITE.test(path) && e.startTime > freshAt + SLACK) markDirty(e.startTime);
      });
    }).observe({ type: 'resource' });
  } catch (e) {}

  // Checkout: wait (at most 4 s) for a running or pending sync and for a theme cart request still on its way,
  // then read the cart one last time and fix it if needed (3 s at most), then submit again.
  let pass = false;
  const pending = () => relevant() && !!(busy || timer || stale() || groupedAt > freshAt + SLACK);
  const within = (p, ms) => Promise.race([p, sleep(ms)]);
  // The /cart form posts one updates[] per row, by position: only when those rows are still the cart's lines.
  const inSync = (form) => {
    const inputs = [...form.elements].filter((el) => el.name === 'updates[]' && !el.disabled);
    if (!inputs.length || !state || !Array.isArray(state.lines)) return true;
    if (inputs.length !== state.lines.length) return false;
    return inputs.every((el, i) => {
      const row = el.closest('[data-key]');
      return (!row || row.dataset.key === state.lines[i][0]) && Number(el.value) === state.lines[i][1];
    });
  };
  window.addEventListener(
    'submit',
    async (e) => {
      const b = e.submitter;
      if (pass || !b || b.name !== 'checkout' || !relevant()) return;
      e.preventDefault();
      const scope = b.closest('[id]');
      const scopeId = scope && scope.id;
      HTML.classList.add('cv-bf-busy');
      const until = Date.now() + 4000;
      while (pending() && Date.now() < until) {
        if (!busy && (timer || stale())) kick();
        await within(busy || sleep(60), Math.max(0, until - Date.now()));
      }
      await within(
        (async () => {
          if (busy) await busy;
          if (await read()) await kick(true);
        })(),
        3000
      );
      HTML.classList.remove('cv-bf-busy');
      const box = scopeId && D.getElementById(scopeId);
      const btn = b.isConnected ? b : box && box.querySelector('[name="checkout"]');
      const form = (btn && btn.form) || e.target;
      if (form && form.elements && !inSync(form)) [...form.elements].forEach((el) => el.name === 'updates[]' && (el.disabled = true));
      pass = true;
      try {
        if (btn && btn.form && form.requestSubmit) form.requestSubmit(btn);
        else if (btn) btn.click();
        else form.submit();
      } finally {
        pass = false;
      }
    },
    true
  );

  const start = () => {
    if (window.cvBfGift) return;
    const el = D.querySelector('script[data-cv-bf-state]');
    accept(parse(el && el.outerHTML), 0);
    if (state) recall();
    shown = sigOf(state);
    window.cvBfGift = {
      state: () => state,
      cap: () => cap,
      inWindow,
      start: START,
      end: END,
      // Re-render the drawer / cart page from the cart as it is (assets/cart.js, when a line key went stale).
      refresh: () => {
        shown = '';
        markDirty(now());
      },
    };
    const ps = window.FoxTheme && window.FoxTheme.pubsub;
    if (ps)
      ps.subscribe(ps.PUB_SUB_EVENTS.cartUpdate, (ev) => {
        if (!ev || ev.sources === SRC || !ev.cart || ev.cart.errors) return;
        const s = ev.cart.sections && parse(ev.cart.sections[SECTION]);
        if (!s) return markDirty(now());
        // The theme has just rendered this response (possibly an older one than we know of).
        shown = sigOf(s);
        accept(s, groupedAt);
        if (state === s && Array.isArray(ev.cart.items)) good = { at: freshAt, cart: ev.cart, sections: ev.cart.sections };
        // Fewer free skins due (a skin removed): correct at once. More: wait a moment for further clicks.
        if (relevant()) schedule(qty(state.gifts) > want() ? 0 : 150);
      });
    window.addEventListener('pageshow', (e) => e.persisted && markDirty(now()));
    let seen = Date.now();
    D.addEventListener('visibilitychange', () => {
      if (D.visibilityState === 'visible' && Date.now() - seen > 15e3) markDirty(now());
      seen = Date.now();
    });
    // The drawer opening after a while: an app may have changed the cart without anyone reading the response.
    const drawer = D.querySelector('cart-drawer');
    if (drawer && window.MutationObserver)
      new MutationObserver(() => drawer.hasAttribute('open') && inWindow() && !pending() && now() - freshAt > 3000 && markDirty(now())).observe(drawer, {
        attributes: true,
        attributeFilter: ['open'],
      });
    // Start / end while the page stays open
    const arm = () => {
      const d = valid ? (Date.now() < START ? START : Date.now() < END ? END : 0) - Date.now() : 0;
      if (d > 0 && d < 2147483000)
        setTimeout(() => {
          cap = Infinity;
          markDirty(now());
          arm();
        }, d + 500);
    };
    arm();
    short();
    if (relevant()) schedule(200);
  };
  if (window.FoxTheme && window.FoxTheme.pubsub) start();
  else {
    D.addEventListener('DOMContentLoaded', start, { once: true });
    window.addEventListener('load', start, { once: true });
  }
})();
